import React, { useRef, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FiPaperclip, FiX } from 'react-icons/fi';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import { LoadingWrapper } from '../../shared/ui/loading-wrapper';
import {
  fetchTicketById,
  solveTicket,
  closeTicket,
  assignTicket,
  updateTicket,
  addTicketMessage,
  addTicketMessageWithFiles,
  resetCurrentTicket,
  clearError,
} from '../../entities/ticket/model';
import {
  useTicketStatusDict,
  TicketStatusDict,
} from '../../shared/lib/ticket-statuses';
import { helpdeskSettingsApi } from '../../api/services/helpdesk-settings.service';
import { ticketsApi } from '../../api/services/tickets.service';
import { MessageTranslation } from '../../types/ticketInterfaces';
import { selectUser } from '../../entities/auth/model';
import { useHdPermissions } from '@shared/lib/ability';
import getTime from '../../entities/issue/lib/get-time';
import {
  TicketStatus,
  TicketMessage as TicketMessageType,
  TICKET_STATUS_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_CHANNEL_LABELS,
  TicketPriority,
  TicketTypeRef,
} from '../../types/ticketInterfaces';
import { SlaIndicator } from '../../shared/ui/sla-indicator';

/* ══════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════ */

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const getUserInitials = (firstName?: string, lastName?: string): string => {
  const f = (firstName ?? '').charAt(0).toUpperCase();
  const l = (lastName ?? '').charAt(0).toUpperCase();
  return (f + l) || '?';
};

const personName = (u: any) => {
  if (!u) return 'Не назначен';
  return [u.last_name, u.first_name].filter(Boolean).join(' ') || u.username || '—';
};

const historyFieldLabel = (f: string): string => {
  const map: Record<string, string> = {
    status: 'Статус', priority: 'Приоритет', assigned_to: 'Ответственный',
    subject: 'Тема', description: 'Описание', company: 'Компания',
  };
  return map[f] || f;
};

const historyDisplayValue = (
  field: string,
  val: string,
  dict?: TicketStatusDict,
): string => {
  if (!val) return '—';
  if (field === 'status') {
    return dict?.labelFor(val) ?? TICKET_STATUS_LABELS[val as TicketStatus] ?? val;
  }
  if (field === 'priority') return TICKET_PRIORITY_LABELS[val as TicketPriority] ?? val;
  return val;
};

/* ══════════════════════════════════════════════
   Status Stepper (like Issue page status style)
   ══════════════════════════════════════════════ */

const STEPS: TicketStatus[] = ['open', 'solved', 'closed'];

function StatusStepper({
  current,
  dict,
}: {
  current: TicketStatus;
  dict: TicketStatusDict;
}) {
  // Кастомный промежуточный статус («В работе» и т.п.) в системный
  // степпер не входит — считаем его этапом «open» (заявка не решена).
  const byCode = new Map(dict.statuses.map((s) => [s.code, s]));
  const cur = byCode.get(current);
  const effective: TicketStatus = STEPS.includes(current)
    ? current
    : cur?.is_final
    ? 'closed'
    : cur?.is_resolved
    ? 'solved'
    : 'open';
  const idx = STEPS.indexOf(effective);
  return (
    <div className="flex items-center gap-3">
      {STEPS.map((step, i) => {
        const done = i <= idx;
        const active = i === idx;
        const color = dict.colorFor(step);
        return (
          <React.Fragment key={step}>
            {i > 0 && (
              <div className="w-8 h-[2px]" style={{ backgroundColor: done ? color : '#ddd' }} />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{
                  backgroundColor: done ? color : '#efefef',
                  color: done ? '#fff' : '#bcbcbc',
                }}
              >
                {done ? '✓' : i + 1}
              </div>
              <span
                className="text-xs font-semibold"
                style={{ color: active ? color : done ? '#242426' : '#bcbcbc' }}
              >
                {dict.labelFor(step)}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════
   Main Component
   ══════════════════════════════════════════════ */

export default function HelpdeskTicket() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: ticketId } = useParams<{ id: string }>();

  const ticket = useAppSelector((state) => state.tickets.currentTicket);
  const status = useAppSelector((state) => state.tickets.status);
  const error = useAppSelector((state) => state.tickets.error);
  const currentUser = useAppSelector(selectUser);
  const hd = useHdPermissions();
  const statusDict = useTicketStatusDict();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isInternal, setIsInternal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showReassign, setShowReassign] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [operators, setOperators] = useState<
    { id: number; full_name: string; username: string }[]
  >([]);
  const [ticketTypes, setTicketTypes] = useState<TicketTypeRef[]>([]);
  const [showTranslated, setShowTranslated] = useState(false);
  const [translations, setTranslations] = useState<Record<number, MessageTranslation>>({});
  
  const [translateNotice, setTranslateNotice] = useState<string | null>(null);

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<{ body: string }>({
    defaultValues: { body: '' },
  });
  const bodyValue = watch('body');

  useEffect(() => {
    dispatch(clearError());
    if (ticketId) dispatch(fetchTicketById(Number(ticketId)));
    return () => { dispatch(resetCurrentTicket()); };
  }, [dispatch, ticketId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [ticket?.messages]);

  useEffect(() => {
    if (!hd.canManageTickets) return;
    helpdeskSettingsApi
      .getTicketTypes(true)
      .then((res) => setTicketTypes(res.data))
      .catch(() => setTicketTypes([]));
  }, [hd.canManageTickets]);

  /* Actions */
  const handleAssignToMe = () => {
    if (ticket && currentUser?.id) dispatch(assignTicket({ ticketId: ticket.id, operatorId: currentUser.id }));
  };
  const handleSolve = () => { if (ticket) dispatch(solveTicket(ticket.id)); };
  const handleClose = () => { if (ticket) dispatch(closeTicket(ticket.id)); };
  const handleSetStatus = (code: string) => {
    if (ticket) dispatch(updateTicket({ id: ticket.id, data: { status: code } }));
    setShowStatusMenu(false);
  };

  const handleSetType = (value: string) => {
    if (ticket) {
      dispatch(
        updateTicket({
          id: ticket.id,
          data: { ticket_type: value ? Number(value) : null },
        }),
      );
    }
  };

  // Кастомные статусы из справочника — все, не только промежуточные:
  // «решённые» и «финальные» тоже должны быть достижимы, иначе созданный
  // в настройках статус остаётся мёртвым (кнопки «Решить»/«Закрыть»
  // переводят только в системные solved/closed).
  // Переход идёт через PATCH — бэк прогоняет смену через ту же
  // бизнес-логику (автоответ клиенту, таймер автозакрытия).
  // Финальные гейтим тем же правом, что и кнопку «Закрыть».
  const customStatusTargets = statusDict
    .statusesFor(ticket?.ticket_type?.id)
    .filter(
      (s) =>
        s.is_active &&
        !s.is_system &&
        s.code !== ticket?.status &&
        (s.is_final ? hd.canReassignTicket : true),
    );

  // Определение текущего статуса из справочника: по нему прячем кнопку
  // «Решить» (fallback на системные коды, пока справочник грузится).
  const currentStatusDef = ticket
    ? statusDict.statuses.find((s) => s.code === ticket.status) ?? {
        is_resolved: ticket.status === 'solved',
        is_final: ticket.status === 'closed',
      }
    : undefined;

  // Список операторов для переназначения. Эндпоинт доступен только тем,
  // у кого есть права на настройки HD; если 403 — оставляем список пустым
  // (кнопка переназначения и так показана лишь admin/lead).
  const loadOperators = async () => {
    if (operators.length) return;
    try {
      const res = await helpdeskSettingsApi.getAvailableOperators();
      setOperators(res.data);
    } catch {
      /* нет доступа к списку операторов */
    }
  };
  const toggleReassign = () => {
    if (!showReassign) loadOperators();
    setShowReassign((v) => !v);
  };
  const handleReassign = (operatorId: number) => {
    if (ticket && operatorId) dispatch(assignTicket({ ticketId: ticket.id, operatorId }));
    setShowReassign(false);
  };

  // Перевод переписки на язык интерфейса пользователя (DeepL на бэке).
  const toggleTranslate = async () => {
    if (!ticket) return;
    setTranslateNotice(null);
    if (showTranslated) {
      setShowTranslated(false);
      return;
    }
    try {
      const lang = (currentUser as any)?.preferred_language || 'ru';
      const res = await ticketsApi.getMessagesTranslated(ticket.id, lang);
      const map: Record<number, MessageTranslation> = {};
      res.data.forEach((m) => {
        if (m.translation) map[m.id] = m.translation;
      });
      // Бэк отвечает 200 и на ненастроенном DeepL — просто возвращает
      // оригинал с is_translated=false для всех сообщений. Раньше это
      // молча показывало оригинал, и казалось, что кнопка сломана.
      const hasAnyTranslation = Object.values(map).some(
        (t) => t.is_translated,
      );
      if (!hasAnyTranslation) {
        setTranslateNotice(
          'Перевод недоступен: сервис перевода не настроен или сообщения уже на выбранном языке.',
        );
        return;
      }
      setTranslations(map);
      setShowTranslated(true);
    } catch {
      setTranslateNotice('Не удалось получить перевод. Попробуйте позже.');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (data: { body: string }) => {
    if (!ticket || !data.body.trim()) return;
    setIsSubmitting(true);
    try {
      if (selectedFiles.length > 0) {
        await dispatch(addTicketMessageWithFiles({ ticketId: ticket.id, body: data.body, files: selectedFiles, isInternal }));
      } else {
        await dispatch(addTicketMessage({ ticketId: ticket.id, data: { body: data.body, is_internal: isInternal } }));
      }
      dispatch(fetchTicketById(ticket.id));
      reset();
      setIsInternal(false);
      setSelectedFiles([]);
    } catch { /* redux */ } finally { setIsSubmitting(false); }
  };

  return (
    <div
      className="grid gap-x-[30px] gap-y-5"
      style={{
        gridTemplateColumns: '2.5fr 2fr',
        gridTemplateRows: '30px auto 1fr',
        gridTemplateAreas: '"back back" "header header" "main sidebar"',
      }}
    >
      {error && (
        <div className="fixed top-4 right-4 z-50 max-w-sm flex items-start gap-3 text-sm text-white bg-[#e3282a] rounded-lg shadow-lg px-4 py-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => dispatch(clearError())}
            className="font-bold leading-none"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
      )}
      <LoadingWrapper status={status}>
        {ticket && (
          <>
            {/* ── Back link (same style as Issue.tsx) ── */}
            <Link
              className="[grid-area:back] text-left font-semibold text-sm text-[#b4b4b4] flex items-center gap-[5px] before:content-[''] before:inline-block before:border-b-2 before:border-l-2 before:border-[#b4b4b4] before:w-2 before:h-2 before:rotate-45 before:mr-[5px] before:align-middle"
              to="/helpdesk"
            >
              Заявки
            </Link>

            {/* ── Header card ── */}
            <div className="[grid-area:header] p-5 bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)] flex items-center justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <p className="text-[#242426] font-bold text-base">
                    #{ticket.number} — {ticket.subject}
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: statusDict.colorFor(ticket.status) }}
                  >
                    {statusDict.labelFor(ticket.status)}
                  </span>
                </div>
                <StatusStepper current={ticket.status} dict={statusDict} />
              </div>
              {/* Операторские действия — видны только HD-staff */}
              {hd.canManageTickets && (
                <div className="relative flex items-center gap-2">
                  <button
                    onClick={handleAssignToMe}
                    className="px-4 py-2.5 text-sm font-semibold text-[#242426] bg-white border border-[#d0d3d8] rounded-lg hover:bg-[#f0f2f4] transition-all duration-300"
                  >
                    Забрать
                  </button>

                  {/* Переназначение на другого оператора — только admin/lead
                      (бэкенд: обычный оператор может назначить лишь себе). */}
                  {hd.canReassignTicket && (
                    <div className="relative">
                      <button
                        onClick={toggleReassign}
                        className="px-4 py-2.5 text-sm font-semibold text-[#242426] bg-white border border-[#d0d3d8] rounded-lg hover:bg-[#f0f2f4] transition-all duration-300"
                      >
                        Переназначить
                      </button>
                      {showReassign && (
                        <div className="absolute right-0 top-full mt-1 z-20 w-[240px] max-h-[280px] overflow-y-auto bg-white border border-[#e5e7eb] rounded-lg shadow-lg py-1">
                          {operators.length === 0 ? (
                            <p className="px-3 py-2 text-sm text-[#bcbcbc]">Список недоступен</p>
                          ) : (
                            operators.map((op) => (
                              <button
                                key={op.id}
                                onClick={() => handleReassign(op.id)}
                                className="w-full text-left px-3 py-2 text-sm text-[#242426] hover:bg-[#f0f2f4] transition-colors"
                              >
                                {op.full_name || op.username}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Закрыть — на бэке требует admin/lead (IsAdminOrLeadOperator).
                      Раньше кнопка показывалась всем canManageTickets, и рядовой
                      оператор ловил 403. Гейтим тем же admin/lead-правом. */}
                  {hd.canReassignTicket && (
                    <button
                      onClick={handleClose}
                      className="px-4 py-2.5 text-sm font-semibold text-[#242426] bg-white border border-[#d0d3d8] rounded-lg hover:bg-[#f0f2f4] transition-all duration-300"
                    >
                      Закрыть
                    </button>
                  )}
                  {/* Кастомные статусы из справочника настроек */}
                  {customStatusTargets.length > 0 && (
                    <div className="relative">
                      <button
                        onClick={() => setShowStatusMenu((v) => !v)}
                        className="px-4 py-2.5 text-sm font-semibold text-[#242426] bg-white border border-[#d0d3d8] rounded-lg hover:bg-[#f0f2f4] transition-all duration-300"
                      >
                        Статус
                      </button>
                      {showStatusMenu && (
                        <div className="absolute right-0 top-full mt-1 z-20 w-[200px] bg-white border border-[#e5e7eb] rounded-lg shadow-lg py-1">
                          {customStatusTargets.map((s) => (
                            <button
                              key={s.code}
                              onClick={() => handleSetStatus(s.code)}
                              className="w-full text-left px-3 py-2 text-sm text-[#242426] hover:bg-[#f0f2f4] transition-colors flex items-center gap-2"
                            >
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: s.color }}
                              />
                              {s.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* «Решить» прячем по семантике справочника, а не по кодам:
                      в кастомном resolved/final статусе кнопка не нужна. */}
                  {!currentStatusDef?.is_resolved && !currentStatusDef?.is_final && (
                    <button
                      onClick={handleSolve}
                      className="px-4 py-2.5 text-sm font-semibold text-white bg-[#E3282A] rounded-lg hover:bg-[#C91F21] transition-all duration-300"
                    >
                      Решить
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ── Main: Description + Chat ── */}
            <div className="[grid-area:main] flex flex-col gap-5">
              {/* Description card */}
              <div className="p-[27px_31px] bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                <p className="text-[#242426] font-bold text-base mb-[15px]">Описание</p>
                <p className="text-sm font-medium text-[#242426] whitespace-pre-wrap break-words">
                  {ticket.description}
                </p>
                {/* Вложения тикета */}
                {ticket.attachments && ticket.attachments.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-[#efefef]">
                    <p className="text-[#bcbcbc] font-semibold text-sm mb-2">
                      Вложения ({ticket.attachments.length})
                    </p>
                    <div className="flex flex-col gap-1.5">
                      {ticket.attachments.map((a) => (
                        <a
                          key={a.id}
                          href={a.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[#f0f2f4] transition-colors"
                        >
                          <FiPaperclip className="w-4 h-4 text-[#bcbcbc]" />
                          <span className="text-sm text-[#e3282a] font-medium truncate">{a.filename}</span>
                          <span className="text-xs text-[#bcbcbc]">{formatFileSize(a.size_bytes)}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Chat card (same pattern as Issue.tsx) */}
              <div className="flex flex-col bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)] min-h-[400px]">
                <div className="flex items-center justify-between px-[27px] pt-[27px] pb-3">
                  <p className="text-[#242426] font-bold text-base">
                    Переписка ({ticket.messages?.length || 0})
                  </p>
                  <button
                    type="button"
                    onClick={toggleTranslate}
                    className="text-sm font-semibold text-[#e3282a] hover:underline"
                  >
                    {showTranslated ? 'Оригинал' : 'Перевести'}
                  </button>
                </div>
                {translateNotice && (
                  <p className="px-[27px] pb-1 text-xs text-[#B26B00]">
                    {translateNotice}
                  </p>
                )}

                {/* Messages */}
                <div
                  ref={scrollRef}
                  className="flex-1 px-[25px] py-[15px] flex flex-col gap-5 overflow-y-auto max-h-[450px] scroll-smooth [scrollbar-color:#e3282a_#fff] [scrollbar-width:thin]"
                >
                  {ticket.messages && ticket.messages.length > 0 ? (
                    ticket.messages
                    .filter((msg) => !msg.is_internal || hd.canAddInternalNote)
                    .map((msg) => {
                      const initials = getUserInitials(msg.author?.first_name, msg.author?.last_name);
                      const isMyMsg = msg.author?.id === currentUser?.id;
                      const isNote = msg.is_internal;

                      return (
                        <div
                          key={msg.id}
                          className={`relative self-${isMyMsg ? 'end' : 'start'} max-w-[400px] flex flex-col gap-[5px] pt-3 pr-8 pb-5 pl-[19px] ${
                            isNote
                              ? 'bg-[rgba(255,193,7,0.12)] rounded-[15px]'
                              : isMyMsg
                                ? 'bg-[rgba(227,40,42,0.12)] rounded-[15px_15px_0_15px]'
                                : 'bg-[#efefef] rounded-[15px_15px_15px_0]'
                          }`}
                        >
                          <div className="text-sm flex justify-between text-[#e3282a] font-semibold gap-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                                style={{ backgroundColor: isNote ? '#FFC107' : isMyMsg ? '#E3282A' : '#3B82F6' }}
                              >
                                {initials}
                              </div>
                              <span className={isNote ? 'text-[#B8860B]' : 'text-[#e3282a]'}>
                                {personName(msg.author)}
                              </span>
                            </div>
                            <span className="font-medium text-[#bcbcbc] whitespace-nowrap text-xs">
                              {getTime(msg.created_at, true, false)?.split(', ')[1] || ''}
                            </span>
                          </div>
                          {isNote && (
                            <span className="text-[10px] font-semibold text-[#B8860B] uppercase tracking-wide">
                              Внутренняя заметка
                            </span>
                          )}
                          <p className="text-[#242426] text-sm font-medium whitespace-pre-wrap break-words">
                            {msg.body}
                          </p>
                          {showTranslated && translations[msg.id]?.is_translated && (
                            <div className="mt-1.5 pt-1.5 border-t border-dashed border-[#d0d3d8]">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#bcbcbc]">
                                Перевод ({translations[msg.id].target_lang})
                              </span>
                              <p className="text-[#242426] text-sm font-medium whitespace-pre-wrap break-words">
                                {translations[msg.id].translated}
                              </p>
                            </div>
                          )}
                          {/* Message attachments */}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="flex flex-col gap-1 mt-1">
                              {msg.attachments.map((a) => (
                                <a
                                  key={a.id}
                                  href={a.file}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2"
                                >
                                  <FiPaperclip className="w-3.5 h-3.5 text-[#bcbcbc]" />
                                  <span className="text-xs text-[#e3282a] truncate">{a.filename}</span>
                                  <span className="text-xs text-[#bcbcbc]">{formatFileSize(a.size_bytes)}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-[#bcbcbc] text-sm text-center py-10">Сообщений пока нет</p>
                  )}
                </div>

                {/* Reply form (same pattern as Issue.tsx form) */}
                <div className="border-t border-[#efefef]">
                  {/* Tabs — вкладка «Внутренняя заметка» видна только HD-staff */}
                  <div className="flex gap-2 px-[25px] pt-3">
                    <button
                      type="button"
                      onClick={() => setIsInternal(false)}
                      className={`py-2 px-1 text-sm font-semibold border-b-2 transition-colors ${
                        !isInternal
                          ? 'text-[#2d2c2c] border-[#e3282a]'
                          : 'text-gray-400 border-transparent hover:text-gray-600'
                      }`}
                    >
                      Ответ клиенту
                    </button>
                    {hd.canAddInternalNote && (
                      <button
                        type="button"
                        onClick={() => setIsInternal(true)}
                        className={`py-2 px-1 text-sm font-semibold border-b-2 transition-colors ${
                          isInternal
                            ? 'text-[#B8860B] border-[#FFC107]'
                            : 'text-gray-400 border-transparent hover:text-gray-600'
                        }`}
                      >
                        Внутренняя заметка
                      </button>
                    )}
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} className="px-[25px] pb-5 pt-3">
                    <div className="flex gap-[1.5%] items-center justify-between">
                      <label className="cursor-pointer m-0 [&_input[type='file']]:hidden">
                        <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} />
                        <FiPaperclip className="w-[28px] h-[28px] text-[#bcbcbc] hover:text-[#e3282a] transition-colors cursor-pointer" />
                      </label>
                      <input
                        {...register('body', { required: 'Невозможно отправить пустое сообщение' })}
                        placeholder={isInternal ? 'Внутренняя заметка...' : 'Введите сообщение...'}
                        className={`py-[15px] px-[10px] border-[1.5px] flex-1 rounded ${
                          isInternal ? 'border-[#FFC107] bg-[rgba(255,193,7,0.05)]' : 'border-[#dcdee0]'
                        }`}
                        autoComplete="off"
                      />
                      <button
                        type="submit"
                        disabled={isSubmitting || !bodyValue?.trim()}
                        className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-[#e3282a] hover:bg-[#C91F21] transition-all duration-300 disabled:bg-[#CCCCCC] disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    {errors.body && (
                      <p className="text-red-500 text-sm mt-1">{errors.body.message}</p>
                    )}
                    {/* Selected files */}
                    {selectedFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedFiles.map((f, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#efefef] rounded text-xs text-[#242426]">
                            <FiPaperclip className="w-3 h-3 text-[#bcbcbc]" />
                            <span className="truncate max-w-[120px]">{f.name}</span>
                            <button type="button" onClick={() => setSelectedFiles(selectedFiles.filter((_, idx) => idx !== i))} className="text-[#bcbcbc] hover:text-[#e3282a]">
                              <FiX className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>

            {/* ── Sidebar ── */}
            <div className="[grid-area:sidebar] flex flex-col gap-5">
              {/* SLA — единый компонент SlaIndicator (detailed). Заменил
                  старую вёрстку с ручным окрашиванием: теперь по обоим
                  дедлайнам видно «осталось N» / «просрочен на N» /
                  «закрыт за N до дедлайна», цвет считается одной палитрой
                  и совпадает с компактным индикатором в списке. */}
              {ticket.sla && (
                <div className="p-[27px_31px] bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                  <p className="text-[#242426] font-bold text-base mb-[15px]">SLA</p>
                  <SlaIndicator
                    sla={ticket.sla}
                    ticketStatus={ticket.status}
                    variant="detailed"
                  />
                </div>
              )}

              {/* Ticket info */}
              <div className="p-[27px_31px] bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                <p className="text-[#242426] font-bold text-base mb-[15px]">Заявка</p>
                <div className="flex flex-col gap-[15px]">
                  <div className="flex flex-col gap-[5px]">
                    <p className="text-[#bcbcbc] font-semibold text-sm">Номер</p>
                    <p className="font-medium text-sm">{ticket.number}</p>
                  </div>
                  <div className="flex flex-col gap-[5px]">
                    <p className="text-[#bcbcbc] font-semibold text-sm">Приоритет</p>
                    <p className="font-medium text-sm">{TICKET_PRIORITY_LABELS[ticket.priority]}</p>
                  </div>
                  <div className="flex flex-col gap-[5px]">
                    <p className="text-[#bcbcbc] font-semibold text-sm">Канал</p>
                    <p className="font-medium text-sm">{TICKET_CHANNEL_LABELS[ticket.channel]}</p>
                  </div>
                  <div className="flex flex-col gap-[5px]">
                    <p className="text-[#bcbcbc] font-semibold text-sm">Тип заявки</p>
                    {hd.canManageTickets ? (
                      <select
                        value={ticket.ticket_type?.id ?? ''}
                        onChange={(e) => handleSetType(e.target.value)}
                        className="font-medium text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
                      >
                        <option value="">— не указан —</option>
                        {ticketTypes.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                        {ticket.ticket_type &&
                          !ticketTypes.some((t) => t.id === ticket.ticket_type!.id) && (
                            <option value={ticket.ticket_type.id}>
                              {ticket.ticket_type.name}
                            </option>
                          )}
                      </select>
                    ) : (
                      <p className="font-medium text-sm">
                        {ticket.ticket_type?.name ?? '—'}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-[5px]">
                    <p className="text-[#bcbcbc] font-semibold text-sm">Дата создания</p>
                    <p className="font-medium text-sm">{getTime(ticket.created_at)}</p>
                  </div>
                  {ticket.solved_at && (
                    <div className="flex flex-col gap-[5px]">
                      <p className="text-[#bcbcbc] font-semibold text-sm">Дата решения</p>
                      <p className="font-medium text-sm">{getTime(ticket.solved_at)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Operator */}
              <div className="p-[27px_31px] bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                <p className="text-[#242426] font-bold text-base mb-[15px]">Ответственный</p>
                <div className="flex items-center gap-[10px]">
                  {ticket.assigned_to ? (
                    <>
                      <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-sm font-bold">
                        {getUserInitials(ticket.assigned_to.first_name, ticket.assigned_to.last_name)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{personName(ticket.assigned_to)}</p>
                        {ticket.assigned_to.email && (
                          <p className="text-[#bcbcbc] text-xs">{ticket.assigned_to.email}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="font-medium text-sm text-[#bcbcbc]">Не назначен</p>
                  )}
                </div>
              </div>

              {/* Client */}
              <div className="p-[27px_31px] bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                <p className="text-[#242426] font-bold text-base mb-[15px]">Клиент</p>
                {ticket.user ? (
                  <div className="flex flex-col gap-[15px]">
                    <div className="flex items-center gap-[10px]">
                      <div className="w-10 h-10 rounded-full bg-[#43A047] flex items-center justify-center text-white text-sm font-bold">
                        {getUserInitials(ticket.user.first_name, ticket.user.last_name)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{personName(ticket.user)}</p>
                        {ticket.user.email && (
                          <p className="text-[#bcbcbc] text-xs">{ticket.user.email}</p>
                        )}
                      </div>
                    </div>
                    {ticket.company && (
                      <div className="flex flex-col gap-[5px]">
                        <p className="text-[#bcbcbc] font-semibold text-sm">Компания</p>
                        <p className="font-medium text-sm">{ticket.company.name}</p>
                      </div>
                    )}
                    {ticket.sender_email && (
                      <div className="flex flex-col gap-[5px]">
                        <p className="text-[#bcbcbc] font-semibold text-sm">Email</p>
                        <p className="font-medium text-sm text-[#e3282a]">{ticket.sender_email}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="font-medium text-sm text-[#bcbcbc]">—</p>
                )}
              </div>

              {/* History */}
              {ticket.history && ticket.history.length > 0 && (
                <div className="p-[27px_31px] bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                  <p className="text-[#242426] font-bold text-base mb-[15px]">История</p>
                  <div className="flex flex-col gap-3 max-h-[250px] overflow-y-auto [scrollbar-width:thin]">
                    {ticket.history.map((h) => (
                      <div key={h.id} className="pb-3 border-b border-[#efefef] last:border-b-0 last:pb-0">
                        <p className="text-[#bcbcbc] text-xs mb-1">{getTime(h.changed_at)}</p>
                        <p className="text-sm font-medium text-[#242426]">
                          {h.changed_by ? personName(h.changed_by) : 'Система'}
                          {' — '}
                          {historyFieldLabel(h.field_name)}
                        </p>
                        {(h.old_value || h.new_value) && (
                          <p className="text-xs mt-0.5">
                            {h.old_value && <span className="line-through text-[#e3282a]">{historyDisplayValue(h.field_name, h.old_value, statusDict)}</span>}
                            {h.old_value && h.new_value && ' → '}
                            {h.new_value && <span className="text-[#43A047]">{historyDisplayValue(h.field_name, h.new_value, statusDict)}</span>}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </LoadingWrapper>
    </div>
  );
}
